// Geometry for the person view's moment deck (BIZY-667): where the ghost cards
// behind the front card sit, and where a card starts or lands when it is
// promoted out of the deck or settles back into it.
//
// The whole illusion rests on one rule: a card's deck-side pose is the same
// whether it is being rendered as a ghost or animated to/from that spot. Derive
// them separately and the hand-off stops reading as one card moving.

/** Compact drops the deck below the card's top edge, which the bee owns. */
export const DECK_COMPACT_GHOST_OFFSET = 8;
/** A card resting in the deck sits a touch smaller than the front card. */
export const DECK_GHOST_SCALE = 0.97;
const DECK_GHOST_SCALE_STEP = 0.018;

export type DeckPose = { rotation: number; scale: number; shiftX: number; shiftY: number };
export type DeckDirection = -1 | 1;

/** The pose a card animates to or from while sitting in the deck. */
export type DeckRestPose = { scale: number; x: number; y: number; rotate: number };

// Deterministic per-moment pseudo-random in [0, 1). Keeps a ghost card's tilt
// stable across re-renders while each newly revealed card gets its own
// rotation/offset as you page through the deck.
export function momentJitter(seed: string, salt: number): number {
	let hash = salt;
	for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
	return (hash >>> 0) / 4294967296;
}

/**
 * Where a card sits while it's still in the deck, at `depth` behind the front.
 * Deriving both the ghost cards and the incoming card's start position from
 * this is what makes the card behind look like it rises into the front slot,
 * rather than a new card fading in over the top of it.
 */
export function deckPose(seed: string, depth: number): DeckPose {
	const depthIndex = Math.max(0, depth - 1);
	const direction = momentJitter(seed, 2) < 0.5 ? -1 : 1;

	return {
		rotation: direction * (1.25 + momentJitter(seed, 1) * 1.75 + depthIndex * 0.35),
		scale: DECK_GHOST_SCALE - depthIndex * DECK_GHOST_SCALE_STEP,
		shiftX: Math.round((momentJitter(seed, 4) - 0.5) * (28 + depthIndex * 12)),
		shiftY: Math.round(6 + momentJitter(seed, 3) * 6 + depthIndex * 6),
	};
}

/**
 * How far a ghost's centre sits below the front card's. Wide ghosts share the
 * front card's centre; compact ghosts drop below the bee at the card's top.
 */
export function ghostCentreOffsetY(pose: DeckPose, isCompact: boolean): number {
	return pose.shiftY + (isCompact ? DECK_COMPACT_GHOST_OFFSET : 0);
}

/**
 * The pose a card holds while it is the top ghost. A card promoted to the front
 * starts here, and one settling back into the deck lands here — on the exact
 * spot its replacement ghost occupies.
 */
export function deckRestPose(pose: DeckPose, isCompact: boolean): DeckRestPose {
	return { scale: pose.scale, x: pose.shiftX, y: ghostCentreOffsetY(pose, isCompact), rotate: pose.rotation };
}

/** Derive navigation direction from stable deck positions, with a fallback for hydration. */
export function deckTransitionDirection(previousIndex: number, nextIndex: number, fallback: DeckDirection = 1): DeckDirection {
	if (previousIndex < 0 || nextIndex < 0 || previousIndex === nextIndex) return fallback;
	return nextIndex > previousIndex ? 1 : -1;
}

/** The peeling card leads forward motion; the returning card leads backward motion. */
export function deckCardLayer(direction: DeckDirection, phase: 'incoming' | 'outgoing'): number {
	if (phase === 'outgoing') return direction === 1 ? 2 : 1;
	return direction === 1 ? 1 : 2;
}
